import React, { useState, useEffect, useRef, useCallback } from 'react';

// The record the person is looking at, read from the address (".../leads/<id>"), so "this lead" means something.
const ROUTES = { leads: 'lead', deals: 'deal', contacts: 'contact', accounts: 'account' };
function recordFromPath(path) {
  const m = /\/dashboard\/crm\/(leads|deals|contacts|accounts)\/([a-f0-9]{24})(?:\/|$)/i.exec(path || '');
  return m ? { entityType: ROUTES[m[1].toLowerCase()], entityId: m[2] } : undefined;
}
import { Bot, Send, X, RotateCcw, Loader, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { cn } from '../../utils/cn';

const GREETING = "Hi! I'm your CRM assistant. Ask me to look up leads, create tasks, schedule events, update deals — anything you'd normally do by hand.";

function Bubble({ role, content }) {
  const isUser = role === 'user';
  return (
    <div className={cn('flex mb-3', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-lg px-3.5 py-2 text-[13px] leading-relaxed whitespace-pre-wrap shadow-sm',
          isUser ? 'bg-[#0176D3] text-white' : 'bg-[#F4F6F9] border border-[#D8DDE6] text-[#080707]'
        )}
      >
        {content}
      </div>
    </div>
  );
}

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null); // { text, showIntegrationsLink }
  const scrollRef = useRef(null);
  const toggleLockRef = useRef(false);

  const toggleOpen = useCallback(() => {
    if (toggleLockRef.current) return;
    toggleLockRef.current = true;
    setOpen((v) => !v);
    setTimeout(() => { toggleLockRef.current = false; }, 350);
  }, []);

  const [status, setStatus] = useState(null); // { activeProvider, creditBalance }

  useEffect(() => {
    if (open && !loaded) {
      api.get('/crm/assistant/history')
        .then((res) => setMessages(res.data?.data?.messages || []))
        .catch(() => {})
        .finally(() => setLoaded(true));

      api.get('/crm/assistant/status')
        .then((res) => setStatus(res.data?.data || null))
        .catch(() => {});
    }
  }, [open, loaded]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending, notice]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setNotice(null);
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await api.post('/crm/assistant/chat', { message: text, context: recordFromPath(window.location.pathname) });
      const reply = res.data?.data?.reply || "I didn't get a response — try again.";
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'NO_LLM_KEY') {
        setNotice({
          text: 'The AI Assistant is not available right now. Please contact support.',
        });
      } else if (code === 'AGENTIC_MODE_REQUIRED') {
        setNotice({
          text: 'Agentic Mode is required. Please upgrade your plan.',
          showBillingLink: false,
        });
      } else if (code === 'LLM_PROVIDER_ERROR') {
        setNotice({
          text: err.response?.data?.error || 'The AI provider had a problem. Please try again in a moment.',
        });
      } else if (code === 'INSUFFICIENT_AI_CREDITS') {
        setNotice({
          text: 'AI Credits are exhausted. Top up credits or upgrade your plan.',
          showBillingLink: true,
        });
      } else {
        setNotice({ text: err.response?.data?.error || 'The assistant ran into an error. Please try again.' });
      }
    } finally {
      setSending(false);
    }
  }, [input, sending]);

  const handleReset = async () => {
    try { await api.post('/crm/assistant/reset'); } catch {}
    setMessages([]);
    setNotice(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-[60]">
      {open && (
        <div className="mb-3 w-[min(360px,calc(100vw-2rem))] h-[520px] bg-white border border-[#D8DDE6] rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200">
          <div className="flex items-center justify-between px-4 py-2.5 bg-[#0176D3] text-white shrink-0">
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5">
                <Sparkles size={15} />
                <span className="text-[13px] font-bold">AI Assistant</span>
              </div>
              {status?.activeProvider && !status.activeProvider.error && (
                <span className="text-[10px] text-white/80 font-medium">
                  {status.activeProvider.provider === 'anthropic' ? 'Claude' : 'OpenAI'} ({status.activeProvider.model || 'default'})
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={handleReset} title="New conversation" aria-label="New conversation" className="p-1.5 hover:bg-white/15 rounded-md transition-colors">
                <RotateCcw size={14} />
              </button>
              <button onClick={toggleOpen} title="Close" aria-label="Close" className="p-1.5 hover:bg-white/15 rounded-md transition-colors">
                <X size={16} />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 bg-white">
            {messages.length === 0 && !sending && (
              <Bubble role="assistant" content={GREETING} />
            )}
            {messages.map((m, i) => (
              <Bubble key={i} role={m.role} content={m.content} />
            ))}
            {sending && (
              <div className="flex items-center gap-2 text-[#706E6B] text-[12px] px-1">
                <Loader size={13} className="animate-spin" /> Thinking...
              </div>
            )}
            {notice && (
              <div className="mt-2 text-[12px] bg-[#FFF4E5] border border-[#FFCD94] text-[#B35F00] rounded-md px-3 py-2">
                {notice.text}
                {notice.showBillingLink && (
                  <div className="mt-1">
                    <Link to="/dashboard/settings/billing?tab=api-credits" onClick={() => setOpen(false)} className="font-bold underline text-[#0176D3]">
                      Top Up Credits →
                    </Link>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-2.5 border-t border-[#D8DDE6] bg-[#F4F6F9] shrink-0">
            <div className="flex items-end gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask me anything about your CRM…"
                rows={1}
                className="flex-1 resize-none bg-white border border-[#D8DDE6] rounded px-3 py-2 text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]/20 max-h-24"
              />
              <button
                onClick={send}
                disabled={!input.trim() || sending}
                className="p-2.5 bg-[#0176D3] text-white rounded-md disabled:opacity-40 hover:bg-[#015FB0] transition-colors shrink-0"
              >
                <Send size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={toggleOpen}
        className="w-14 h-14 rounded-full bg-[#0176D3] text-white shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex items-center justify-center hover:bg-[#015FB0] hover:scale-105 transition-all"
        title="AI Assistant" aria-label="AI Assistant"
      >
        {open ? <X size={22} /> : <Bot size={24} />}
      </button>
    </div>
  );
}
